import { db } from '../../db/indexeddb'
import {
  getCloudHabits,
  getCloudHabitIds,
  getCloudHabitsByIds,
  getDeletedCloudHabits,
  createCloudHabit,
  updateCloudHabit,
  deleteCloudHabit,
} from '../../supabase/queries'
import { CloudHabitSchema, CloudHabitPayloadSchema } from '../sync-schemas'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { Habit } from '../../db/models'
import { parseLocalDate, toDateString } from './dates'

/* ── Habit ─────────────────────────────────────────── */

// Standalone user-owned entity (like Goal). habit_logs depend on it, so habits
// MUST be pushed before habit_logs (see sync-engine push order).

export const habitPullDescriptor: PullDescriptor<any> = {
  entityName: 'habit',
  settingsKey: 'lastHabitSyncTime',

  fetchRemote: (userId, since) => getCloudHabits(userId, since),
  fetchRemoteIds: (userId) => getCloudHabitIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudHabitsByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudHabits(userId, since),

  safeParse: (raw) => CloudHabitSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.habits,
  getAllLocal: () => db.habits.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.habits.filter(h => !h.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote) => ({
    id: crypto.randomUUID(),
    title: remote.title,
    notes: remote.notes ?? null,
    lifeSpace: remote.life_space,
    frequency: remote.frequency,
    startDate: parseLocalDate(remote.start_date),
    rewardId: remote.reward_id ?? null,
    targetMinutes: remote.target_minutes ?? null,
    pausedAt: remote.paused_at ? new Date(remote.paused_at) : null,
    sortOrder: remote.sort_order ?? 0,
    userId: remote.user_id,
    cloudId: remote.id,
    deletedAt: null,
    importedFrom: remote.imported_from ?? null,
    createdAt: new Date(remote.created_at),
    updatedAt: new Date(remote.updated_at),
    lastModified: new Date(remote.updated_at),
    synced: true,
    syncedAt: new Date(),
  }),

  buildLocalUpdate: async (remote) => ({
    title: remote.title,
    notes: remote.notes ?? null,
    lifeSpace: remote.life_space,
    frequency: remote.frequency,
    startDate: parseLocalDate(remote.start_date),
    rewardId: remote.reward_id ?? null,
    targetMinutes: remote.target_minutes ?? null,
    pausedAt: remote.paused_at ? new Date(remote.paused_at) : null,
    sortOrder: remote.sort_order ?? 0,
    ...('imported_from' in remote ? { importedFrom: remote.imported_from ?? null } : {}),
    updatedAt: new Date(remote.updated_at),
  }),
}

/**
 * Die Herkunft (`testdata:…`, #269) nur, wenn es eine gibt. Die Spalte kam erst mit
 * `20261006120000_goals_habits_imported_from.sql`: Eine Cloud ohne sie lehnte sonst jeden Push ab, auch den
 * echter Einträge, und nach `MAX_RETRIES` Fehlschlägen wäre die Änderung verloren. Geleert wird sie nie.
 */
const importedFromField = (habit: Pick<Habit, 'importedFrom'>) => (habit.importedFrom ? { imported_from: habit.importedFrom } : {})

export const habitPushDescriptor: PushDescriptor<Habit> = {
  entityName: 'CloudHabit',
  getLocal: (id) => db.getHabit(id),
  getLocalCloudId: (h) => h.cloudId,
  getLocalId: (h) => h.id,
  table: db.habits,

  buildCreatePayload: async (habit) => CloudHabitPayloadSchema.parse({
    title: habit.title,
    notes: habit.notes,
    life_space: habit.lifeSpace,
    frequency: habit.frequency,
    start_date: toDateString(habit.startDate),
    reward_id: habit.rewardId,
    target_minutes: habit.targetMinutes,
    paused_at: habit.pausedAt ? habit.pausedAt.toISOString() : null,
    sort_order: habit.sortOrder,
    local_id: habit.id,
    ...importedFromField(habit),
  }),

  buildUpdatePayload: async (habit) => {
    const payload: Record<string, any> = CloudHabitPayloadSchema.parse({
      title: habit.title,
      notes: habit.notes,
      life_space: habit.lifeSpace,
      frequency: habit.frequency,
      start_date: toDateString(habit.startDate),
      reward_id: habit.rewardId,
      target_minutes: habit.targetMinutes,
      paused_at: habit.pausedAt ? habit.pausedAt.toISOString() : null,
      sort_order: habit.sortOrder,
      local_id: habit.id,
      ...importedFromField(habit),
    })
    // paused_at is always sent (incl. null) so un-pausing reaches Supabase.
    if (!habit.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudHabit(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudHabit(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudHabit(cloudId, userId),
}
